import type {
    IPubSubService,
    IServiceManager,
    ServiceConfig,
    ServiceContext,
    ServiceFactory,
    ServiceRegistration,
} from './types';

/**
 * Central registry for visualization services.
 * Provides dependency injection, lazy instantiation, and lifecycle management.
 *
 * @example
 * ```typescript
 * const manager = new ServiceManager();
 *
 * // Register services
 * manager.registerService(
 *     CenterlineService.SERVICE_NAME,
 *     (ctx) => new CenterlineService(ctx)
 * );
 *
 * // Get service (instantiated on first access)
 * const centerlineService = manager.getService<CenterlineService>(
 *     CenterlineService.SERVICE_NAME
 * );
 *
 * // Reset all services when closing a case
 * manager.resetAll();
 *
 * // Dispose when unmounting the visualization module
 * manager.dispose();
 * ```
 */
export class ServiceManager implements IServiceManager {
    /** Registered service factories */
    private factories: Map<string, ServiceFactory<IPubSubService>> = new Map();

    /** Service configurations */
    private configs: Map<string, ServiceConfig> = new Map();

    /** Instantiated service instances (lazy initialization) */
    private instances: Map<string, IPubSubService> = new Map();

    /**
     * Register a service factory.
     * Services are instantiated lazily on first access via getService().
     *
     * @param name - Unique identifier for the service
     * @param factory - Factory function that creates the service instance
     * @param config - Optional service-specific configuration
     * @throws Error if a service with the same name is already registered
     *
     * @example
     * ```typescript
     * manager.registerService(
     *     ViewportService.SERVICE_NAME,
     *     (ctx) => new ViewportService(ctx),
     *     { maxViewports: 4 }
     * );
     * ```
     */
    registerService<T extends IPubSubService>(
        name: string,
        factory: ServiceFactory<T>,
        config?: ServiceConfig
    ): void {
        if (this.factories.has(name)) {
            throw new Error(`Service "${name}" is already registered`);
        }

        this.factories.set(name, factory as ServiceFactory<IPubSubService>);
        if (config) {
            this.configs.set(name, config);
        }
    }

    /**
     * Register multiple services at once.
     * Accepts an array of registrations, optionally paired with configurations.
     *
     * @param services - Array of service registrations or [registration, config] tuples
     *
     * @example
     * ```typescript
     * manager.registerServices([
     *     { name: "ServiceA", factory: (ctx) => new ServiceA(ctx) },
     *     [{ name: "ServiceB", factory: (ctx) => new ServiceB(ctx) }, { option: true }],
     * ]);
     * ```
     */
    registerServices(services: Array<ServiceRegistration | [ServiceRegistration, ServiceConfig]>): void {
        for (const entry of services) {
            if (Array.isArray(entry)) {
                const [registration, config] = entry;
                this.registerService(registration.name, registration.factory, config);
            } else {
                this.registerService(entry.name, entry.factory);
            }
        }
    }

    /**
     * Get a service by name.
     * The service is instantiated on first access (lazy initialization).
     *
     * @param name - The service name
     * @returns The service instance
     * @throws Error if the service is not registered
     *
     * @example
     * ```typescript
     * const centerline = manager.getService<CenterlineService>(
     *     CenterlineService.SERVICE_NAME
     * );
     * ```
     */
    getService<T extends IPubSubService>(name: string): T {
        // Return cached instance if exists
        if (this.instances.has(name)) {
            return this.instances.get(name) as T;
        }

        // Get factory
        const factory = this.factories.get(name);
        if (!factory) {
            throw new Error(`Service "${name}" is not registered`);
        }

        // Create context and instantiate
        const context: ServiceContext = {
            services: this,
            config: this.configs.get(name),
        };

        const instance = factory(context);
        this.instances.set(name, instance);

        return instance as T;
    }

    /**
     * Check if a service is registered.
     *
     * @param name - The service name to check
     * @returns true if the service is registered
     */
    hasService(name: string): boolean {
        return this.factories.has(name);
    }

    /**
     * Reset all instantiated services.
     * Calls reset() on each service to clear subscriptions and state.
     * Use this when closing a case to prepare for loading a new one.
     */
    resetAll(): void {
        this.instances.forEach((service) => service.reset());
    }

    /**
     * Dispose all services and clear all registrations.
     * Should be called when the visualization module is unmounted.
     */
    dispose(): void {
        this.resetAll();
        this.instances.clear();
        this.factories.clear();
        this.configs.clear();
    }
}
